import { useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import {
  Loader2,
  ScrollText,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  LogIn,
  Edit,
  Trash2,
  Plus,
  Eye,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { auditLogService, AuditLog, ListAuditLogsFilter } from "../../services/auditLogService";
import { useAuth } from "../../contexts/AuthContext";

const LIMIT = 30;

const CATEGORIAS = [
  "Autenticação",
  "Militares",
  "Configuração",
  "Perfis",
  "Grupos",
  "Membros de Grupo",
  "Ações de Grupo",
  "Turmas",
  "Notas",
  "Relatórios de Treinamento",
  "Relatórios",
  "Honrarias",
  "Medalhas",
  "Emblemas",
  "Loja",
  "Desempenho",
  "Votação de Destaque",
  "Fila de Treinamento",
  "RM",
  "Inscrições",
  "Justificativas de Ausência",
];

const METODO_CORES: Record<string, string> = {
  POST: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  PUT: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  PATCH: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  DELETE: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  GET: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const STATUS_COR = (code: number | null) => {
  if (!code) return "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400";
  if (code < 300) return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
  if (code < 400) return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
  if (code === 429) return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400";
  return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
};

function MetodoIcone({ metodo }: { metodo: string }) {
  if (metodo === "DELETE") return <Trash2 className="w-3.5 h-3.5" />;
  if (metodo === "POST") return <Plus className="w-3.5 h-3.5" />;
  if (metodo === "PUT" || metodo === "PATCH") return <Edit className="w-3.5 h-3.5" />;
  if (metodo === "GET") return <Eye className="w-3.5 h-3.5" />;
  return null;
}

// A frase já vem pronta do backend (campo descricao).
// Esta função só é usada como fallback caso descricao esteja vazio.
function fraseFallback(log: AuditLog): string {
  const verbos: Record<string, string> = {
    POST: "criou", PUT: "editou", PATCH: "editou", DELETE: "excluiu", GET: "consultou",
  };
  const verbo = verbos[log.metodo] ?? log.metodo.toLowerCase();
  return `Ação: ${verbo} em ${log.rota}`;
}

function formatarData(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Cache de geolocalização — persiste enquanto a aba estiver aberta. */
const geoCache = new Map<string, string>();

const PRIVATE_IP = /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|::1$)/;

async function resolveGeo(ip: string): Promise<string> {
  if (!ip || PRIVATE_IP.test(ip)) return "";
  if (geoCache.has(ip)) return geoCache.get(ip)!;
  try {
    const data = await fetch(`https://ipwho.is/${ip}`).then((r) => r.json());
    const location =
      data.success && data.city && data.region ? `${data.city}, ${data.region}` : "";
    geoCache.set(ip, location);
    return location;
  } catch {
    geoCache.set(ip, "");
    return "";
  }
}

export function Logs() {
  const { enqueueSnackbar } = useSnackbar();
  const { militar } = useAuth();

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [geoMap, setGeoMap] = useState<Record<string, string>>({});

  // Filtros
  const [filDescricao, setFilDescricao] = useState("");
  const [filIp, setFilIp] = useState("");
  const [filMetodo, setFilMetodo] = useState("");
  const [filCategoria, setFilCategoria] = useState("");
  const [filDataInicio, setFilDataInicio] = useState("");
  const [filDataFim, setFilDataFim] = useState("");

  // Filtros aplicados
  const [filtros, setFiltros] = useState<ListAuditLogsFilter>({});

  const totalPages = Math.ceil(total / LIMIT);

  const carregarLogs = useCallback(
    async (f: ListAuditLogsFilter, p: number) => {
      setLoading(true);
      try {
        const result = await auditLogService.listar({ ...f, page: p, limit: LIMIT });
        setLogs(result.logs);
        setTotal(result.total);
      } catch {
        enqueueSnackbar("Erro ao carregar logs", { variant: "error" });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    carregarLogs(filtros, page);
  }, [filtros, page, carregarLogs]);

  useEffect(() => {
    const uniqueIps = Array.from(new Set(logs.map((l) => l.ip).filter(Boolean)));
    if (uniqueIps.length === 0) return;
    Promise.all(uniqueIps.map((ip) => resolveGeo(ip).then((loc) => ({ ip, loc })))).then(
      (results) => {
        setGeoMap((prev) => {
          const next = { ...prev };
          results.forEach(({ ip, loc }) => { if (loc) next[ip] = loc; });
          return next;
        });
      }
    );
  }, [logs]);

  function aplicarFiltros() {
    const f: ListAuditLogsFilter = {};
    if (filDescricao.trim()) f.descricao = filDescricao.trim();
    if (filIp.trim()) f.ip = filIp.trim();
    if (filMetodo) f.metodo = filMetodo;
    if (filCategoria) f.categoria = filCategoria;
    if (filDataInicio) f.data_inicio = filDataInicio;
    if (filDataFim) f.data_fim = new Date(filDataFim + "T23:59:59").toISOString();
    setFiltros(f);
    setPage(1);
  }

  function limparFiltros() {
    setFilDescricao("");
    setFilIp("");
    setFilMetodo("");
    setFilCategoria("");
    setFilDataInicio("");
    setFilDataFim("");
    setFiltros({});
    setPage(1);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <ScrollText className="w-6 h-6" />
          Logs de Auditoria
        </h1>
        <p className="text-muted-foreground mt-1">
          Histórico de ações realizadas no painel — criações, edições, exclusões e acessos.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filtros */}
        <div className="lg:col-span-1">
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)] sticky top-20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtros
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Descrição</label>
                <Input
                  placeholder="Buscar na descrição..."
                  value={filDescricao}
                  onChange={(e) => setFilDescricao(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">IP</label>
                <Input
                  placeholder="Ex: 192.168.0.1"
                  value={filIp}
                  onChange={(e) => setFilIp(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Método</label>
                <select
                  className="w-full h-8 text-sm border border-input bg-background rounded-md px-2 text-foreground"
                  value={filMetodo}
                  onChange={(e) => setFilMetodo(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                  <option value="GET">GET</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Categoria</label>
                <select
                  className="w-full h-8 text-sm border border-input bg-background rounded-md px-2 text-foreground"
                  value={filCategoria}
                  onChange={(e) => setFilCategoria(e.target.value)}
                >
                  <option value="">Todas</option>
                  {CATEGORIAS.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Data início</label>
                <Input
                  type="date"
                  value={filDataInicio}
                  onChange={(e) => setFilDataInicio(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Data fim</label>
                <Input
                  type="date"
                  value={filDataFim}
                  onChange={(e) => setFilDataFim(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              <Button
                className="w-full h-8 text-sm"
                onClick={aplicarFiltros}
              >
                <Search className="w-3.5 h-3.5 mr-2" />
                Buscar
              </Button>

              {Object.keys(filtros).length > 0 && (
                <Button
                  variant="outline"
                  className="w-full h-8 text-sm"
                  onClick={limparFiltros}
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-2" />
                  Limpar
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Stats */}
          <Card className="mt-4">
            <CardContent className="pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total de registros</span>
                <span className="font-medium">{total.toLocaleString("pt-BR")}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Página</span>
                <span className="font-medium">{page} / {totalPages || 1}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lista de logs */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Registros</CardTitle>
              <CardDescription>
                {total > 0
                  ? `${((page - 1) * LIMIT) + 1}–${Math.min(page * LIMIT, total)} de ${total.toLocaleString("pt-BR")} registros`
                  : "Nenhum registro encontrado"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center items-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : logs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
                  <AlertCircle className="w-10 h-10 opacity-40" />
                  <p className="text-sm">Nenhum log encontrado para os filtros aplicados.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => {
                    const frase = log.descricao ?? fraseFallback(log);

                    return (
                      <div
                        key={log.id}
                        className="flex flex-col sm:flex-row sm:items-start gap-3 p-4 border border-border rounded-lg hover:bg-muted/40 transition-colors"
                      >
                        {/* Método badge */}
                        <div className="flex-shrink-0 flex items-center gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              METODO_CORES[log.metodo] ?? METODO_CORES.GET
                            }`}
                          >
                            <MetodoIcone metodo={log.metodo} />
                            {log.metodo}
                          </span>
                          {log.status_code && (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono ${STATUS_COR(log.status_code)}`}
                            >
                              {log.status_code}
                            </span>
                          )}
                        </div>

                        {/* Conteúdo principal */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground font-medium leading-snug">
                            {frase}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                            {log.rota}
                          </p>
                          {log.categoria && (
                            <span className="inline-block mt-1 text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              {log.categoria}
                            </span>
                          )}
                        </div>

                        {/* Metadados */}
                        <div className="flex-shrink-0 text-right space-y-0.5">
                          <p className="text-xs text-muted-foreground">
                            {formatarData(log.criado_em)}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {log.ip}
                          </p>
                          {geoMap[log.ip] && (
                            <p className="text-xs text-muted-foreground">
                              {geoMap[log.ip]}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Paginação */}
              {!loading && totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
